import { addDays, getDay, getDaysInMonth, getMonth, getYear, isAfter, isBefore, isSameDay, isValid, parse } from 'date-fns';

/**
 * Returns the parsed date(s) that can be processed using [date-fns](https://date-fns.org/) library.
 *
 * @param {(array|any)} dates - One date or an array of dates to be parsed,
 * can be an instanse of Date, a string, or an array of either one of the previous.
 * @param {string} dateFormat - The format of given date(s)
 * @returns {(array|string)}
 */
export var getParsedDates = function getParsedDates(dates, dateFormat) {
  if (Array.isArray(dates)) {
    return dates.map(function (value) {
      if (typeof value === 'string') {
        return parse(value, dateFormat, new Date());
      }
      return value;
    });
  }
  return typeof dates === 'string' ? parse(dates, dateFormat, new Date()) : dates;
};

/**
 * Returns a valid year number based on min and max year.
 *
 * @param {Date} date - The date that provides year to be compared
 * @param {number} minYear - The min year
 * @param {number} maxYear - The max year
 * @returns {number}
 */
export var getValidYear = function getValidYear(date, minYear, maxYear) {
  return Math.min(Math.max(getYear(date), minYear), maxYear);
};

/**
 * Returns a 2D array that will be used to render calendar days in the given month and year.
 *
 * @param {number} targetMonth - The targetted month
 * @param {number} targetYear - The targetted year
 * @returns {array}
 */
export var getMonthMatrix = function getMonthMatrix(targetMonth, targetYear) {
  var daysInMonth = getDaysInMonth(new Date(targetYear, targetMonth));
  var firstDayOfWeek = getDay(new Date(targetYear, targetMonth));
  var monthMatrix = [[]];

  // push null if day(s) belong to the previous month
  for (var i = 0; i < firstDayOfWeek; i += 1) {
    monthMatrix[0].push(null);
  }

  // push days to the matrix by month
  for (var _i = 0; _i < daysInMonth; _i += 1) {
    if (monthMatrix[monthMatrix.length - 1].length === 7) {
      monthMatrix.push([]);
    }
    monthMatrix[monthMatrix.length - 1].push(_i + 1);
  }

  // push days if day(s) belong to the next month
  for (var _i2 = monthMatrix[monthMatrix.length - 1].length; _i2 < 7; _i2 += 1) {
    monthMatrix[monthMatrix.length - 1].push(null);
  }

  // if there are only 5 rows, push days to reserve the last row
  if (monthMatrix.length === 5) {
    monthMatrix.push([null, null, null, null, null, null, null]);
  }
  return monthMatrix;
};

/**
 * Construct an array based on the start, stop, and step value.
 *
 * @param {number} start - The min value in the array
 * @param {number} stop - The max value in the array
 * @param {number} step - The gap between two adjacent values
 * @returns {array}
 */
export var getArrayFromRange = function getArrayFromRange(start, stop, step) {
  return Array.from({
    length: (stop - start) / step + 1
  }, function (_, i) {
    return start + i * step;
  });
};

/**
 * If a date is between the min date and max date.
 *
 * @param {Date} newDate - The date to be compared
 * @param {Date} minDate - The min date available to be selected
 * @param {Date} maxDate - The max date available to be selected
 * @returns {boolean}
 */
export var isDateInBetween = function isDateInBetween(newDate, minDate, maxDate) {
  return (isAfter(newDate, minDate) || isSameDay(newDate, minDate)) && (isBefore(newDate, maxDate) || isSameDay(newDate, maxDate));
};

/**
 * If a date is a disabled date.
 *
 * @param {Date} date - The date to be compared
 * @param {array} disabledDates - An array of disabled dates
 * @param {number} year - The current year
 * @param {number} month - The current month
 * @param {Date} minDate - The minimum date available
 * @param {Date} maxDate - The maximum date available
 * @returns {boolean}
 */
export var isDisabledDate = function isDisabledDate(date, disabledDates, minDate, maxDate) {
  if (typeof disabledDates === 'function') {
    return disabledDates(date, {
      minDate: minDate,
      maxDate: maxDate
    });
  }
  return isBefore(date, minDate) || isAfter(date, maxDate) || disabledDates.some(function (value) {
    return isSameDay(date, value);
  });
};

/**
 * Return the first available date to be focusable in the current month and year.
 *
 * @param {Date} date - The date to be evaluated
 * @param {array} disabledDates - An array of disabled dates
 * @param {Date} minDate - The minimum date available
 * @param {Date} maxDate - The maximum date available
 * @returns {Date|null}
 */
export var getFirstAvailableDate = function getFirstAvailableDate(givenDate, disabledDates, minDate, maxDate) {
  // If the date given is invalid, use the current date
  var date = givenDate;
  if (!isValid(date)) {
    date = new Date();
  }
  if (!isDisabledDate(date, disabledDates, minDate, maxDate)) {
    return date;
  }

  // Iterate over days until we find a non disabled days, or we reach the max date
  var year = getYear(date);
  var month = getMonth(date);
  date = new Date(year, month, 1);
  while (isDisabledDate(date, disabledDates, minDate, maxDate) && date.getMonth() === month) {
    date = addDays(date, 1);
  }

  // Check again if valid (we might have hit end of month or all invalid dates)
  if (!isDisabledDate(date, disabledDates, minDate, maxDate) && date.getMonth() === month) {
    return date;
  }

  // Return the first day of the month
  return new Date(year, month, 1);
};
export var getNextAvailableDay = function getNextAvailableDay(givenDate, disabledDates, minDate, maxDate) {
  var date = givenDate;
  do {
    date = addDays(date, 1);
  } while (isDisabledDate(date, disabledDates, minDate, maxDate) && isBefore(date, maxDate));

  // Check again if valid (we might have hit all invalid dates)
  if (!isDisabledDate(date, disabledDates, minDate, maxDate)) {
    return date;
  }
  return null;
};
export var getPreviousAvailableDay = function getPreviousAvailableDay(givenDate, disabledDates, minDate, maxDate) {
  var date = givenDate;
  do {
    date = addDays(date, -1);
  } while (isDisabledDate(date, disabledDates, minDate, maxDate) && isAfter(date, minDate));

  // Check again if valid (we might have hit all invalid dates)
  if (!isDisabledDate(date, disabledDates, minDate, maxDate)) {
    return date;
  }
  return null;
};